// Third Party
import * as React from 'react';
import { Wrapper } from '@googlemaps/react-wrapper';
import dynamic from 'next/dynamic';

// Utils
import getConfig from '@config';

// UI
import palette from '../../../color-palette.json';
import { Typography } from '@components/typography';
import {
  Map,
  MapMarker,
  MapCircle,
  MapMarkerDefaultIcon,
} from '@components/map';
import { MapProvider } from '@components/map/map-context';
import { formatListingAddress } from '@utils/format-listing-address';

type Props = {
  lat: number;
  lng: number;
  addressLineOne?: string;
  city?: string;
  postalCode?: string;
  stateOrProvinceLongName?: string;
  locationObfuscated: boolean;
};

/**
 * This module relies on the class google.maps.OverlayView which
 * is only available on the window once the google maps script has loaded.
 */
const MapOverlay = dynamic(
  () => import('@pages-components/search/search-map/map-overlay'),
  {
    ssr: false,
  }
);

export const MapPreview: React.FC<Props> = ({
  addressLineOne,
  city,
  postalCode,
  stateOrProvinceLongName,
  lat,
  lng,
  locationObfuscated,
}) => {
  const {
    publicRuntimeConfig: {
      google: { apiKey },
    },
  } = getConfig();

  const address = formatListingAddress({
    addressLineOne: locationObfuscated ? '' : addressLineOne,
    city: city,
    postalCode: postalCode,
    stateOrProvinceLongName: stateOrProvinceLongName,
  });

  const [infoCardOpen, setInfoCardOpen] = React.useState<boolean>(false);

  return (
    <div>
      <Typography
        component="h4"
        variantM="title4"
        variant="subtitle2"
        className="pb-4"
        data-testid="@map/listing-address-name"
      >
        {address}
      </Typography>
      {locationObfuscated && (
        <Typography
          component="p"
          variantM="body2"
          variant="small"
          className="pb-4"
          data-testid="@map/listing-address-location"
        >
          Location provided after the reservation is made.
        </Typography>
      )}

      <div className="h-[350px]" data-testid="@map/map-area">
        <Wrapper apiKey={apiKey}>
          <MapProvider>
            <Map
              style={{
                flexGrow: '1',
                height: '100%',
                borderRadius: '6px',
              }}
              center={{ lat: lat, lng: lng }}
              zoom={11}
              clickableIcons={true}
            >
              {locationObfuscated && (
                <MapCircle
                  center={{ lat: lat, lng: lng }}
                  radius={750}
                  fillColor={palette.primary.main}
                  strokeColor={palette.primary.light}
                  strokeOpacity={0.4}
                  strokePosition={2.0}
                  fillOpacity={0.8}
                  strokeWeight={100}
                  clickable={true}
                  onClick={() => {
                    setInfoCardOpen(true);
                  }}
                />
              )}
              {!locationObfuscated && (
                <MapMarker
                  position={{ lat: lat, lng: lng }}
                  cursor="pointer"
                  icon={MapMarkerDefaultIcon}
                />
              )}
              {infoCardOpen && lat && lng && (
                <MapOverlay
                  position={{
                    lat: lat + 0.01,
                    lng: lng + 0.0125,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: palette.gray.main,
                      borderRadius: '2px',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      color: palette.white,
                      minWidth: 250,
                      textAlign: 'center',
                      padding: 8,
                      zIndex: 1005,
                    }}
                  >
                    You will receive the exact location after booking
                  </div>
                </MapOverlay>
              )}
            </Map>
          </MapProvider>
        </Wrapper>
      </div>
    </div>
  );
};
